/**
 * Status → CSS-variable + color mapping for Producer map markers.
 *
 * Stream A PR 2 / commit M2 — the design-system anchor for marker
 * styling. The wire shape carries ``health_state`` as a SAPIENT
 * enum **integer** (0..4 per ``schemas/proto/meridian/v1/data/
 * status.proto``); operator-facing color is resolved through the
 * design-system tokens at
 * ``docs/engineering/detail/storyboards/shared/design-system.md``
 * §"Semantic / status".
 *
 * # Why a runtime CSS-variable read
 *
 * Stream B wires the dark theme (REQ-UI-040 / REQ-UI-045) by
 * flipping the design-system tokens at the ``:root`` (or
 * ``[data-theme="dark"]``) selector. If marker colors were
 * hard-coded as hex literals the dark theme would silently regress.
 * Reading the CSS custom property at runtime via
 * :func:`getComputedStyle` lets the same WebGL layer paint pick up
 * the active theme's value when the marker re-paints.
 *
 * Per REQ-UI-046 the **semantic distinguishability** (healthy /
 * degraded / unhealthy / unknown / stale) survives both palettes;
 * the hex values shift between themes but the meaning does not.
 *
 * # Why the proto's OFFLINE maps to ``--status-unhealthy``
 *
 * The SAPIENT enum names a value ``HEALTH_STATE_OFFLINE`` (3); the
 * design-system token names it ``--status-unhealthy``. They name
 * the same operator-facing concept — "offline" is the storyboard's
 * everyday term, "unhealthy" is the design-system's category. See
 * ``design-system.md`` §"Semantic / status" — the row for
 * ``--status-unhealthy`` reads "Offline platform, error toast,
 * critical".
 *
 * # ``--status-stale`` as a UI-derived state
 *
 * ``stale`` is not a SAPIENT proto enum value — there is no
 * ``HEALTH_STATE_STALE``. It's a UI-derived overlay applied when
 * the latest status message is older than a threshold but the
 * platform was previously healthy / degraded. M2 ships the token
 * mapping so Stream B's dark theme keeps the seam; the stale-derive
 * logic lands with M3 (SSE) where data-freshness becomes a real
 * concept on the client side.
 *
 * Cross-references:
 *   - REQ-UI-002 (WebGL marker rendering — color is part of the
 *     paint spec).
 *   - REQ-UI-040 (paired light/dark palettes).
 *   - REQ-UI-046 (status colors survive both themes).
 *   - docs/engineering/detail/storyboards/shared/design-system.md.
 *   - schemas/proto/meridian/v1/data/status.proto §HealthState.
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md
 *     §"Map markers and glyphs".
 */
import type { ExpressionSpecification } from "maplibre-gl";

/**
 * SAPIENT ``HealthState`` enum integer values.
 *
 * Mirrors ``schemas/proto/meridian/v1/data/status.proto`` so the
 * UI doesn't depend on the protobuf-generated TypeScript (which
 * would pull in a runtime dep we don't otherwise need). The integer
 * values are part of the wire contract (CLAUDE.md §4.2 — protobuf
 * field/enum numbers are immutable); adding a new state means a
 * Lane B api-change-request per the meridian-ui-engineer overlay.
 *
 * Cross-references:
 *   - schemas/proto/meridian/v1/data/status.proto.
 */
export const HEALTH_STATE = {
  UNSPECIFIED: 0,
  HEALTHY: 1,
  DEGRADED: 2,
  OFFLINE: 3,
  UNKNOWN: 4,
} as const;

/**
 * Type of any valid :const:`HEALTH_STATE` integer value.
 */
export type HealthState = (typeof HEALTH_STATE)[keyof typeof HEALTH_STATE];

/**
 * UI-side categorical names for marker styling.
 *
 * The proto carries integers on the wire; the design-system tokens
 * are named categorical. This type names the bridge so callers can
 * pass either a numeric ``health_state`` or a derived category
 * (e.g., ``"stale"``, which has no proto representation).
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/shared/design-system.md.
 */
export type MarkerStatus = "healthy" | "degraded" | "unhealthy" | "unknown" | "stale";

/**
 * CSS custom-property names from
 * ``docs/engineering/detail/storyboards/shared/design-system.md``
 * §"Semantic / status".
 *
 * Each variable resolves to a hex color via the global stylesheet
 * (``ui/src/styles/globals.css``). Stream B's PR adds the paired
 * ``[data-theme="dark"]`` overrides per REQ-UI-040; this map is
 * theme-agnostic by design.
 */
export const STATUS_CSS_VARIABLE_MAP: Readonly<Record<MarkerStatus, string>> = {
  healthy: "--status-healthy",
  degraded: "--status-degraded",
  unhealthy: "--status-unhealthy",
  unknown: "--status-unknown",
  stale: "--status-stale",
};

/**
 * Hard-coded fallback hex values, dark-theme, sourced from
 * ``design-system.md`` §"Color" 2026-05-26 amendment
 * (Appaloosa-aligned aesthetic refresh).
 *
 * Used when :func:`getComputedStyle` is unavailable (SSR / jsdom
 * without applied styles) or returns an empty string. The fallback
 * keeps the WebGL paint deterministic — operator-visibility
 * (CLAUDE.md §8.1) over a blank canvas. Per the 2026-05-26
 * amendment, production is dark-first; the fallback matches the
 * dark palette so the rare-path render visually agrees with the
 * runtime default.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/shared/design-system.md
 *     2026-05-26 amendment.
 */
export const STATUS_FALLBACK_HEX_MAP: Readonly<Record<MarkerStatus, string>> = {
  healthy: "#3ecf8e",
  // Deep orange — moved off amber (#f5a623) so degraded no longer collides
  // with the --brand amber selection ring in dark (2026-05-30). Matches the
  // dark ``--status-degraded`` token in globals.css.
  degraded: "#ea580c",
  unhealthy: "#e5534b",
  unknown: "#8b92aa",
  stale: "#4f566b",
};

/**
 * Map the SAPIENT ``HealthState`` enum integer to the UI marker
 * category.
 *
 * Per ``status.proto`` §HealthState:
 *   - 0 ``HEALTH_STATE_UNSPECIFIED`` → ``"unknown"``. The
 *     proto default — the Producer didn't populate the field;
 *     operator semantics match the "no recent contact" interpretation.
 *   - 1 ``HEALTH_STATE_HEALTHY`` → ``"healthy"``.
 *   - 2 ``HEALTH_STATE_DEGRADED`` → ``"degraded"``.
 *   - 3 ``HEALTH_STATE_OFFLINE`` → ``"unhealthy"``. Design-system's
 *     ``--status-unhealthy`` row names "Offline platform".
 *   - 4 ``HEALTH_STATE_UNKNOWN`` → ``"unknown"``.
 *
 * Unknown integer values (e.g., a future enum value added in a
 * Lane B cascade before this UI is regenerated) fall back to
 * ``"unknown"`` so the marker still paints rather than crashing —
 * forward-compatibility per CLAUDE.md §4.2 (additive evolution).
 *
 * Cross-references:
 *   - REQ-UI-046.
 *   - schemas/proto/meridian/v1/data/status.proto.
 */
export function healthStateToMarkerStatus(state: number): MarkerStatus {
  switch (state) {
    case HEALTH_STATE.HEALTHY:
      return "healthy";
    case HEALTH_STATE.DEGRADED:
      return "degraded";
    case HEALTH_STATE.OFFLINE:
      return "unhealthy";
    case HEALTH_STATE.UNSPECIFIED:
    case HEALTH_STATE.UNKNOWN:
      return "unknown";
    default:
      // Unknown enum integer — log loudly and degrade to "unknown"
      // so the marker still renders. Operator-visibility per
      // CLAUDE.md §8.1: silent fallbacks are forbidden. Stream B+
      // can swap to a toast if the operator-facing surface needs
      // explicit signal.
      console.warn(
        `[meridian/map/marker-styling] unknown HealthState integer: ${state}; ` +
          'mapping to "unknown"',
      );
      return "unknown";
  }
}

/**
 * Resolve the active hex color for a marker category.
 *
 * Reads :const:`STATUS_CSS_VARIABLE_MAP` against ``:root`` via
 * :func:`getComputedStyle`. If the variable is undefined (test
 * environment without applied styles), falls back to
 * :const:`STATUS_FALLBACK_HEX_MAP`. The runtime read means Stream
 * B's theme flip (REQ-UI-040) takes effect on the next marker
 * paint without re-mounting the layer.
 *
 * @param status - The marker category.
 * @returns A CSS color string suitable for MapLibre's ``paint``
 *     spec (e.g., ``"#10b981"``, ``"rgb(16, 185, 129)"``).
 *
 * Cross-references:
 *   - REQ-UI-040 / REQ-UI-045 (theme).
 *   - REQ-UI-046 (semantic distinguishability across themes).
 */
export function getStatusColor(status: MarkerStatus): string {
  if (typeof document === "undefined") {
    return STATUS_FALLBACK_HEX_MAP[status];
  }
  const root = document.documentElement;
  const cssVar = STATUS_CSS_VARIABLE_MAP[status];
  const resolved = getComputedStyle(root).getPropertyValue(cssVar).trim();
  if (resolved === "") {
    return STATUS_FALLBACK_HEX_MAP[status];
  }
  return resolved;
}

// ---- Per-value colour overrides (inc-items 4/5 — REQ-UI-086 / REQ-UI-088) ----

/**
 * A sparse per-value colour-override map (the wire shapes
 * ``map.platform.status_colors`` / ``map.alert.type_colors`` /
 * ``map.alert.priority_colors``; ADR-033 §9.2/§9.7). Each value is a validated
 * ``#RRGGBB`` literal; a key that is **absent** means "use the design-system
 * token default for that value". The map is kept **sparse** — only keys the
 * operator explicitly overrode appear, so an un-overridden value always tracks
 * the live theme token (REQ-UI-040 / REQ-UI-046).
 *
 * The **key vocabulary** is owned by the UI (the BFF treats keys as free
 * strings) and MUST be identical between the legend writer
 * (:file:`profiles/profile-editor.tsx`) and the render readers below, so a saved
 * Profile's overrides survive reload:
 *
 *   - **platform ``status_colors``** → the :type:`MarkerStatus` ids
 *     (``healthy`` / ``degraded`` / ``unhealthy`` / ``unknown`` / ``stale``).
 *   - **alert ``type_colors``** → the SAPIENT ``AlertType`` **enum names,
 *     lowercased** (:const:`ALERT_TYPE_OVERRIDE_KEY`): ``information`` /
 *     ``warning`` / ``critical`` / ``error`` / ``fatal`` / ``mode_change``.
 *   - **alert ``priority_colors``** → the SAPIENT ``AlertPriority`` **enum
 *     names, lowercased** (:const:`ALERT_PRIORITY_OVERRIDE_KEY`): ``low`` /
 *     ``medium`` / ``high`` / ``critical``.
 *
 * Enum **names** (not the numeric wire ints, and not the ``--alert-*-*`` token
 * suffixes — which differ for ``mode_change`` vs. ``--alert-type-mode-change``)
 * are the stable key: the proto enum names are the contract (CLAUDE.md §4.2), so
 * a key minted from the name is durable across a UI regen even if a token is
 * renamed.
 */
export type OverrideColors = Readonly<Record<string, string>>;

/**
 * A stable, order-independent string key for a sparse override map, for use as a
 * React effect dependency (the layer repaint must fire when an override changes,
 * but the map is an object whose identity churns each render). Keys are sorted
 * so ``{a,b}`` and ``{b,a}`` hash identically (never a spurious re-paint), and a
 * missing/empty map collapses to ``""``.
 *
 * Cross-references:
 *   - REQ-UI-086 / REQ-UI-088 (per-value overrides repaint in place).
 */
export function stableOverrideKey(overrides?: OverrideColors): string {
  if (overrides === undefined) {
    return "";
  }
  const keys = Object.keys(overrides).sort();
  if (keys.length === 0) {
    return "";
  }
  return keys.map((k) => `${k}=${overrides[k]}`).join("&");
}

/**
 * Resolve a per-value override hex, or ``undefined`` when the value is not
 * overridden / the override is blank.
 *
 * The override map is sparse + operator-supplied, so a present-but-blank value
 * (a malformed Profile) is treated as "no override" rather than painting an
 * empty colour (CLAUDE.md §8.1). Shared by the three render readers below so the
 * "override-first, else token" precedence is written once (CLAUDE.md §4.4).
 *
 * @param overrides - The sparse override map (possibly ``undefined``).
 * @param key - The stable string key for the value.
 * @returns The trimmed override hex, or ``undefined`` to fall back to the token.
 */
function overrideOrUndefined(
  overrides: OverrideColors | undefined,
  key: string,
): string | undefined {
  const raw = overrides?.[key];
  const trimmed = raw?.trim();
  return trimmed === undefined || trimmed === "" ? undefined : trimmed;
}

/**
 * Resolve a platform marker's ``by_status`` colour, honouring a per-status
 * override map FIRST, then falling back to the design-system ``--status-*``
 * token via :func:`getStatusColor` (REQ-UI-086 / REQ-UI-046).
 *
 * @param status - The marker category (its id is the override key).
 * @param overrides - The sparse ``map.platform.status_colors`` map.
 * @returns The override hex when present, else the live status token.
 */
export function resolveStatusColor(status: MarkerStatus, overrides?: OverrideColors): string {
  return overrideOrUndefined(overrides, status) ?? getStatusColor(status);
}

/**
 * Resolve the per-category fill opacity used by the WebGL circle
 * layer.
 *
 * Per Flow 02 README §"Map markers and glyphs":
 *
 *   - healthy / degraded / unhealthy / stale: filled circle (opacity 1).
 *   - unknown: open circle with stroke only (fill opacity 0).
 *
 * The visual contract is "no fill" for unknown to distinguish a
 * platform that hasn't reported yet from one that has — the storyboard
 * names this as "Open grey circle (1 px stroke)".
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md
 *     §"Map markers and glyphs".
 */
export function getStatusFillOpacity(status: MarkerStatus): number {
  return status === "unknown" ? 0 : 1;
}

/**
 * Geometry-overlay kinds painted by the PR5b SAPIENT geometry polygon
 * layers (REQ-UI-072).
 *
 * Each kind maps to a desaturated fill token (a faint semi-transparent
 * wash; the layer applies ``fill-opacity`` ~0.12–0.2) plus a
 * more-saturated stroke token so the polygon outline stays
 * operator-visible (CLAUDE.md §8.1) once the fill drops to a wash:
 *
 *   - ``fov`` — a sensor's field-of-view sector (blue).
 *   - ``coverage`` — a coverage / range footprint (gold).
 *   - ``obscuration`` — a masked / obscured region (violet).
 *
 * The three hues are semantically distinct from each other and from
 * every ``--status-*`` map colour (REQ-UI-046), so a geometry wash is
 * never confused with a platform health state.
 *
 * Cross-references:
 *   - REQ-UI-072 (SAPIENT map geometry).
 *   - ADR-032 (SAPIENT Detection/Alert/Registration data model).
 */
export type GeometryKind = "fov" | "coverage" | "obscuration";

/**
 * CSS custom-property names for the SAPIENT map-overlay glyph + geometry
 * tokens (REQ-UI-072), declared in :file:`src/styles/globals.css` in
 * both the dark ``:root`` and light ``:root[data-theme="light"]``
 * blocks.
 *
 * Split into the two glyph accents (``--detection-glyph`` /
 * ``--alert-glyph``), the three geometry fills (``--geom-*``), and the
 * three geometry strokes (``--geom-*-stroke``). Resolved at paint time
 * via :func:`getComputedStyle` exactly like :const:`STATUS_CSS_VARIABLE_MAP`
 * so the theme flip (REQ-UI-040) carries through without re-mounting the
 * layer.
 */
export const DETECTION_GLYPH_CSS_VARIABLE = "--detection-glyph";

/** CSS custom-property name for the alert glyph accent (REQ-UI-072). */
export const ALERT_GLYPH_CSS_VARIABLE = "--alert-glyph";

/** Geometry-fill CSS custom-property names by kind (REQ-UI-072). */
export const GEOM_FILL_CSS_VARIABLE_MAP: Readonly<Record<GeometryKind, string>> = {
  fov: "--geom-fov",
  coverage: "--geom-coverage",
  obscuration: "--geom-obscuration",
};

/** Geometry-stroke CSS custom-property names by kind (REQ-UI-072). */
export const GEOM_STROKE_CSS_VARIABLE_MAP: Readonly<Record<GeometryKind, string>> = {
  fov: "--geom-fov-stroke",
  coverage: "--geom-coverage-stroke",
  obscuration: "--geom-obscuration-stroke",
};

/**
 * Hard-coded fallback hex for the detection glyph accent, dark-theme
 * (REQ-UI-072) — mirrors the dark ``--detection-glyph`` literal in
 * globals.css. Used when :func:`getComputedStyle` is unavailable (SSR /
 * jsdom without applied styles) or returns an empty string, the same
 * rare-path discipline as :const:`STATUS_FALLBACK_HEX_MAP` (dark-first
 * per the 2026-05-26 design-system amendment).
 */
export const DETECTION_GLYPH_FALLBACK_HEX = "#22d3ee";

/** Fallback hex for the alert glyph accent, dark-theme (REQ-UI-072). */
export const ALERT_GLYPH_FALLBACK_HEX = "#e879f9";

/**
 * Hard-coded fallback hex for the geometry **fill** tokens, dark-theme
 * (REQ-UI-072) — mirrors the dark ``--geom-*`` literals in globals.css.
 * Same SSR/test rare-path role as :const:`STATUS_FALLBACK_HEX_MAP`.
 */
export const GEOM_FILL_FALLBACK_HEX_MAP: Readonly<Record<GeometryKind, string>> = {
  fov: "#5b8fd6",
  coverage: "#d8a838",
  obscuration: "#b07ad6",
};

/**
 * Hard-coded fallback hex for the geometry **stroke** tokens, dark-theme
 * (REQ-UI-072) — mirrors the dark ``--geom-*-stroke`` literals in
 * globals.css.
 */
export const GEOM_STROKE_FALLBACK_HEX_MAP: Readonly<Record<GeometryKind, string>> = {
  fov: "#7aa8e0",
  coverage: "#f0c14b",
  obscuration: "#c79ae6",
};

/**
 * Resolve the active hex for a CSS custom property against ``:root``,
 * falling back to a literal when unavailable.
 *
 * Shared core of the SAPIENT-overlay readers below — the same
 * :func:`getComputedStyle`-with-fallback pattern as
 * :func:`getStatusColor`, factored out so the four public readers
 * (detection glyph, alert glyph, geometry fill, geometry stroke) don't
 * each repeat it (CLAUDE.md §4.4 — no duplicated logic). The runtime
 * read means the theme flip (REQ-UI-040) takes effect on the next paint
 * without re-mounting the layer.
 *
 * @param cssVar - The CSS custom-property name (e.g., ``"--geom-fov"``).
 * @param fallbackHex - The literal returned when ``document`` is
 *     undefined (SSR/tests) or the variable resolves empty.
 * @returns A CSS colour string suitable for MapLibre's ``paint`` spec.
 *
 * Cross-references:
 *   - REQ-UI-040 / REQ-UI-046 (theme + distinguishability).
 *   - REQ-UI-072 (SAPIENT map geometry).
 */
function resolveCssColor(cssVar: string, fallbackHex: string): string {
  if (typeof document === "undefined") {
    return fallbackHex;
  }
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  return resolved === "" ? fallbackHex : resolved;
}

/**
 * Resolve the active hex for the SAPIENT **detection** glyph accent
 * (REQ-UI-072).
 *
 * Reads ``--detection-glyph`` via :func:`getComputedStyle`, falling back
 * to :const:`DETECTION_GLYPH_FALLBACK_HEX` (SSR/tests). A cyan accent,
 * semantically distinct from every ``--status-*`` hue and from the alert
 * glyph, and WCAG-AA legible against both themes' map backgrounds
 * (REQ-UI-046).
 *
 * @returns A CSS colour string for MapLibre's ``paint`` spec.
 *
 * Cross-references:
 *   - REQ-UI-072 (SAPIENT map geometry) / REQ-UI-046 (legibility) /
 *     REQ-UI-040 (theme seam).
 */
export function getDetectionGlyphColor(): string {
  return resolveCssColor(DETECTION_GLYPH_CSS_VARIABLE, DETECTION_GLYPH_FALLBACK_HEX);
}

/**
 * Resolve the active hex for the SAPIENT **alert** glyph accent
 * (REQ-UI-072).
 *
 * Reads ``--alert-glyph`` via :func:`getComputedStyle`, falling back to
 * :const:`ALERT_GLYPH_FALLBACK_HEX` (SSR/tests). A magenta accent that
 * reads as "attention" but is deliberately **not** the
 * ``--status-unhealthy`` red, so an alert glyph never reads as an
 * offline platform (REQ-UI-046).
 *
 * @returns A CSS colour string for MapLibre's ``paint`` spec.
 *
 * Cross-references:
 *   - REQ-UI-072 / REQ-UI-046 / REQ-UI-040.
 */
export function getAlertGlyphColor(): string {
  return resolveCssColor(ALERT_GLYPH_CSS_VARIABLE, ALERT_GLYPH_FALLBACK_HEX);
}

/**
 * Resolve the active **fill** hex for a SAPIENT geometry kind
 * (REQ-UI-072).
 *
 * Reads the ``--geom-{kind}`` token via :func:`getComputedStyle`,
 * falling back to :const:`GEOM_FILL_FALLBACK_HEX_MAP` (SSR/tests). The
 * returned value is the **solid base** hex; the geometry layer applies
 * ``fill-opacity`` (~0.12–0.2) to render the faint wash. The blue / gold
 * / violet triad is distinct per kind and from every ``--status-*`` hue
 * (REQ-UI-046).
 *
 * @param kind - The geometry overlay kind.
 * @returns A CSS colour string for MapLibre's ``fill-color`` paint.
 *
 * Cross-references:
 *   - REQ-UI-072 / REQ-UI-046 / REQ-UI-040.
 */
export function getGeomColor(kind: GeometryKind): string {
  return resolveCssColor(GEOM_FILL_CSS_VARIABLE_MAP[kind], GEOM_FILL_FALLBACK_HEX_MAP[kind]);
}

/**
 * Resolve the active **stroke** hex for a SAPIENT geometry kind
 * (REQ-UI-072).
 *
 * Reads the ``--geom-{kind}-stroke`` token via :func:`getComputedStyle`,
 * falling back to :const:`GEOM_STROKE_FALLBACK_HEX_MAP` (SSR/tests). The
 * stroke is a more-saturated / more-opaque companion to the matching
 * fill so the polygon outline stays operator-visible (CLAUDE.md §8.1)
 * once the fill drops to a wash. WCAG-AA legible in both themes
 * (REQ-UI-046).
 *
 * @param kind - The geometry overlay kind.
 * @returns A CSS colour string for MapLibre's ``line-color`` paint.
 *
 * Cross-references:
 *   - REQ-UI-072 / REQ-UI-046 / REQ-UI-040.
 */
export function getGeomStrokeColor(kind: GeometryKind): string {
  return resolveCssColor(GEOM_STROKE_CSS_VARIABLE_MAP[kind], GEOM_STROKE_FALLBACK_HEX_MAP[kind]);
}

// ---- Platform single-colour (inc6b — REQ-UI-086) -------------------------

/**
 * Resolve the active hex for an arbitrary Profile-supplied marker colour
 * (``map.platform.single_color`` / ``map.detection.single_color`` /
 * ``map.alert.single_color``; ADR-033 §9.2/§9.7).
 *
 * The Profile stores a **literal hex string** (e.g. ``"#3ecf8e"``), so unlike
 * the design-system tokens there is no CSS variable to read — the value is
 * already MapLibre-paint-ready. This thin wrapper exists so callers express the
 * intent ("resolve the operator's chosen single colour") symmetrically with
 * :func:`getStatusColor` & friends, and so a future indirection (e.g. clamping
 * / validating the hex) has one seam. A blank / undefined value falls back to
 * ``fallback`` (the design-system default the Profile schema also defaults to),
 * so a malformed Profile never paints an empty colour (CLAUDE.md §8.1).
 *
 * @param hex - The Profile's single-colour hex, possibly ``undefined`` / blank.
 * @param fallback - The design-system default to use when ``hex`` is absent.
 * @returns A CSS colour string for MapLibre's ``paint`` spec.
 *
 * Cross-references:
 *   - REQ-UI-086 / REQ-UI-087 / REQ-UI-088 (Profile-driven marker colour).
 *   - ADR-033 §9.2 (platform) / §9.7 (alert) single-colour fields.
 */
export function resolveSingleColor(hex: string | undefined, fallback: string): string {
  const trimmed = hex?.trim();
  return trimmed === undefined || trimmed === "" ? fallback : trimmed;
}

/**
 * The platform marker colour config the Map reads from the active Profile's
 * ``map.platform`` slice (ADR-033 §9.2; REQ-UI-086) and threads into the
 * snapshot + cluster colour expressions.
 *
 * - ``mode: "by_status"`` (the Profile default) → colour by ``health_state``
 *   via the semantic status palette (the historical behaviour, unchanged).
 * - ``mode: "single"`` → every platform marker (and every cluster bubble) uses
 *   ``singleColor`` regardless of status.
 *
 * Both fields are optional so a call site without the working state (bare
 * ``renderHook`` tests, the legacy two-arg ``attach*`` calls) gets the
 * ``by_status`` default with the design-system fallback — a strict superset of
 * the pre-inc6b behaviour.
 *
 * Cross-references:
 *   - REQ-UI-086 (platform colour mode); ADR-033 §9.2.
 */
export interface PlatformColorConfig {
  /** ``"by_status"`` (default) or ``"single"``. */
  mode?: "by_status" | "single";
  /** Profile ``map.platform.single_color`` hex — used only in ``single`` mode. */
  singleColor?: string;
  /**
   * Sparse ``map.platform.status_colors`` per-:type:`MarkerStatus` overrides
   * (keyed by the status id) — used only in ``by_status`` mode (inc-items 4/5;
   * REQ-UI-086). Absent / empty ⇒ every status tracks its ``--status-*`` token.
   * Threaded into the snapshot layer's ``by_status`` ``match`` via
   * :func:`resolveStatusColor`.
   */
  statusColors?: OverrideColors;
}

/**
 * If ``config`` selects ``single`` mode, resolve the single colour the platform
 * markers + clusters should paint (REQ-UI-086); otherwise ``null`` so the caller
 * falls through to its ``by_status`` expression.
 *
 * The ``single`` fallback is the **healthy** status hex — the Profile schema
 * defaults ``platform.single_color`` to ``#3ecf8e`` (== dark ``--status-healthy``),
 * so a ``single`` mode with a blank colour still paints the same green the
 * schema default intends (CLAUDE.md §8.1 — never an empty paint).
 *
 * Exported so the snapshot circle layer and the cluster bubble layer share the
 * exact same single-colour decision (CLAUDE.md §4.4 — no duplicated logic),
 * each only owning its distinct ``by_status`` branch.
 *
 * @param config - The Profile-derived platform colour config (possibly empty).
 * @returns The resolved single colour, or ``null`` for ``by_status`` mode.
 *
 * Cross-references:
 *   - REQ-UI-086 / REQ-UI-046; ADR-033 §9.2.
 */
export function platformSingleColorOrNull(config?: PlatformColorConfig): string | null {
  if (config?.mode !== "single") {
    return null;
  }
  return resolveSingleColor(config.singleColor, getStatusColor("healthy"));
}

// ---- Alert severity palettes (inc6b — REQ-UI-088) ------------------------

/**
 * SAPIENT ``AlertType`` enum integer values
 * (``schemas/proto/meridian/v1/data/alert.proto`` §AlertType): 0 UNSPECIFIED /
 * 1 INFORMATION / 2 WARNING / 3 CRITICAL / 4 ERROR / 5 FATAL / 6 MODE_CHANGE.
 *
 * Mirrored here (not pulled from the protobuf-generated TS) for the same reason
 * as :const:`HEALTH_STATE` — the integers are part of the wire contract
 * (CLAUDE.md §4.2). The Map alert-glyph layer keys its ``by_alert_type`` paint
 * on these (REQ-UI-088); the operator-facing labels live in
 * :func:`alerts/alert-display.alertTypeLabel`.
 *
 * Cross-references:
 *   - schemas/proto/meridian/v1/data/alert.proto §AlertType.
 *   - ADR-033 §9.7 (alert colour modes).
 */
export const ALERT_TYPE = {
  UNSPECIFIED: 0,
  INFORMATION: 1,
  WARNING: 2,
  CRITICAL: 3,
  ERROR: 4,
  FATAL: 5,
  MODE_CHANGE: 6,
} as const;

/**
 * SAPIENT ``AlertPriority`` enum integer values
 * (``schemas/proto/meridian/v1/data/alert.proto`` §AlertPriority): 0 UNSPECIFIED
 * / 1 LOW / 2 MEDIUM / 3 HIGH / 4 CRITICAL. The Map alert-glyph layer keys its
 * ``by_priority`` paint on these (REQ-UI-088); the badge labels +
 * severity-bucket mapping live in
 * :func:`alerts/alert-display.describeAlertPriority`.
 *
 * Cross-references:
 *   - schemas/proto/meridian/v1/data/alert.proto §AlertPriority.
 *   - ADR-029 §5 (priority → severity); ADR-033 §9.7.
 */
export const ALERT_PRIORITY = {
  UNSPECIFIED: 0,
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  CRITICAL: 4,
} as const;

/**
 * Stable override-map key for each SAPIENT ``AlertType`` integer — the enum
 * **name, lowercased** (REQ-UI-088; see :type:`OverrideColors`). Keyed by the
 * wire int so the render reader (which switches on the feature's numeric
 * ``alert_type``) can look up the operator's per-type override; the legend
 * writer mints the same key. ``UNSPECIFIED`` is omitted — it is the proto
 * "no value" default, never an operator-meaningful override target, and is not
 * surfaced as a legend row.
 *
 * Names (not the ``--alert-type-*`` token suffixes) are the key: ``MODE_CHANGE``
 * → ``mode_change`` (the token is ``--alert-type-mode-change``), so the key is
 * anchored on the immutable proto enum name rather than a renamable token.
 */
export const ALERT_TYPE_OVERRIDE_KEY: Readonly<Record<number, string>> = {
  [ALERT_TYPE.INFORMATION]: "information",
  [ALERT_TYPE.WARNING]: "warning",
  [ALERT_TYPE.CRITICAL]: "critical",
  [ALERT_TYPE.ERROR]: "error",
  [ALERT_TYPE.FATAL]: "fatal",
  [ALERT_TYPE.MODE_CHANGE]: "mode_change",
};

/**
 * Stable override-map key for each SAPIENT ``AlertPriority`` integer — the enum
 * **name, lowercased** (REQ-UI-088; see :type:`OverrideColors`): ``low`` /
 * ``medium`` / ``high`` / ``critical``. ``UNSPECIFIED`` is omitted (same
 * rationale as :const:`ALERT_TYPE_OVERRIDE_KEY`).
 */
export const ALERT_PRIORITY_OVERRIDE_KEY: Readonly<Record<number, string>> = {
  [ALERT_PRIORITY.LOW]: "low",
  [ALERT_PRIORITY.MEDIUM]: "medium",
  [ALERT_PRIORITY.HIGH]: "high",
  [ALERT_PRIORITY.CRITICAL]: "critical",
};

/**
 * CSS custom-property names for the per-``AlertType`` severity palette
 * (REQ-UI-088), declared in :file:`src/styles/globals.css` in both the dark
 * ``:root`` and light ``:root[data-theme="light"]`` blocks. Keyed by the
 * SAPIENT ``AlertType`` integer so the paint builder can map each enum stop to
 * its token. Resolved at paint time via :func:`getComputedStyle` exactly like
 * :const:`STATUS_CSS_VARIABLE_MAP`, so a theme flip (REQ-UI-040) carries through.
 */
export const ALERT_TYPE_CSS_VARIABLE_MAP: Readonly<Record<number, string>> = {
  [ALERT_TYPE.UNSPECIFIED]: "--alert-type-unspecified",
  [ALERT_TYPE.INFORMATION]: "--alert-type-information",
  [ALERT_TYPE.WARNING]: "--alert-type-warning",
  [ALERT_TYPE.CRITICAL]: "--alert-type-critical",
  [ALERT_TYPE.ERROR]: "--alert-type-error",
  [ALERT_TYPE.FATAL]: "--alert-type-fatal",
  [ALERT_TYPE.MODE_CHANGE]: "--alert-type-mode-change",
};

/**
 * CSS custom-property names for the per-``AlertPriority`` severity palette
 * (REQ-UI-088), declared in :file:`src/styles/globals.css`. Keyed by the SAPIENT
 * ``AlertPriority`` integer.
 */
export const ALERT_PRIORITY_CSS_VARIABLE_MAP: Readonly<Record<number, string>> = {
  [ALERT_PRIORITY.UNSPECIFIED]: "--alert-priority-unspecified",
  [ALERT_PRIORITY.LOW]: "--alert-priority-low",
  [ALERT_PRIORITY.MEDIUM]: "--alert-priority-medium",
  [ALERT_PRIORITY.HIGH]: "--alert-priority-high",
  [ALERT_PRIORITY.CRITICAL]: "--alert-priority-critical",
};

/**
 * Hard-coded fallback hex for the per-``AlertType`` palette, dark-theme
 * (REQ-UI-088) — mirrors the dark ``--alert-type-*`` literals in globals.css.
 * Used when :func:`getComputedStyle` is unavailable (SSR / jsdom without applied
 * styles) or returns an empty string, the same rare-path discipline as
 * :const:`STATUS_FALLBACK_HEX_MAP` (dark-first per the 2026-05-26 design-system
 * amendment). If a token's dark value changes, update both globals.css + this
 * map (CLAUDE.md §4.2 — contracts move together).
 */
export const ALERT_TYPE_FALLBACK_HEX_MAP: Readonly<Record<number, string>> = {
  [ALERT_TYPE.UNSPECIFIED]: "#8b92aa",
  [ALERT_TYPE.INFORMATION]: "#4d9de0",
  [ALERT_TYPE.WARNING]: "#f5a623",
  [ALERT_TYPE.CRITICAL]: "#ff5b52",
  [ALERT_TYPE.ERROR]: "#d23b3b",
  [ALERT_TYPE.FATAL]: "#b266ff",
  [ALERT_TYPE.MODE_CHANGE]: "#c5c9d6",
};

/**
 * Hard-coded fallback hex for the per-``AlertPriority`` palette, dark-theme
 * (REQ-UI-088) — mirrors the dark ``--alert-priority-*`` literals in
 * globals.css.
 */
export const ALERT_PRIORITY_FALLBACK_HEX_MAP: Readonly<Record<number, string>> = {
  [ALERT_PRIORITY.UNSPECIFIED]: "#8b92aa",
  [ALERT_PRIORITY.LOW]: "#4d9de0",
  [ALERT_PRIORITY.MEDIUM]: "#f5a623",
  [ALERT_PRIORITY.HIGH]: "#ff5b52",
  [ALERT_PRIORITY.CRITICAL]: "#d23b3b",
};

/**
 * Resolve the active hex for a SAPIENT ``AlertType`` integer (REQ-UI-088).
 *
 * Reads the ``--alert-type-*`` token via :func:`getComputedStyle`, falling back
 * to :const:`ALERT_TYPE_FALLBACK_HEX_MAP` (SSR/tests). An unknown integer (a
 * future enum addition this UI predates) resolves to the UNSPECIFIED hue rather
 * than throwing — forward-compatibility per CLAUDE.md §8.3, mirroring
 * :func:`alerts/alert-display.alertTypeLabel`.
 *
 * @param alertType - The SAPIENT ``AlertType`` integer.
 * @returns A CSS colour string for MapLibre's ``paint`` spec.
 *
 * Cross-references:
 *   - REQ-UI-088 / REQ-UI-046 / REQ-UI-040 / REQ-UI-094.
 */
export function getAlertTypeColor(alertType: number): string {
  const cssVar =
    ALERT_TYPE_CSS_VARIABLE_MAP[alertType] ?? ALERT_TYPE_CSS_VARIABLE_MAP[ALERT_TYPE.UNSPECIFIED];
  const fallback =
    ALERT_TYPE_FALLBACK_HEX_MAP[alertType] ?? ALERT_TYPE_FALLBACK_HEX_MAP[ALERT_TYPE.UNSPECIFIED];
  return resolveCssColor(cssVar, fallback);
}

/**
 * Resolve the active hex for a SAPIENT ``AlertPriority`` integer (REQ-UI-088).
 *
 * Reads the ``--alert-priority-*`` token via :func:`getComputedStyle`, falling
 * back to :const:`ALERT_PRIORITY_FALLBACK_HEX_MAP` (SSR/tests). An unknown
 * integer resolves to the UNSPECIFIED hue (forward-compatibility, CLAUDE.md
 * §8.3), mirroring :func:`alerts/alert-display.describeAlertPriority`.
 *
 * @param priority - The SAPIENT ``AlertPriority`` integer.
 * @returns A CSS colour string for MapLibre's ``paint`` spec.
 *
 * Cross-references:
 *   - REQ-UI-088 / REQ-UI-046 / REQ-UI-040 / REQ-UI-094 / ADR-029 §5.
 */
export function getAlertPriorityColor(priority: number): string {
  const cssVar =
    ALERT_PRIORITY_CSS_VARIABLE_MAP[priority] ??
    ALERT_PRIORITY_CSS_VARIABLE_MAP[ALERT_PRIORITY.UNSPECIFIED];
  const fallback =
    ALERT_PRIORITY_FALLBACK_HEX_MAP[priority] ??
    ALERT_PRIORITY_FALLBACK_HEX_MAP[ALERT_PRIORITY.UNSPECIFIED];
  return resolveCssColor(cssVar, fallback);
}

/**
 * Resolve a SAPIENT ``AlertType`` integer's colour, honouring a per-type
 * override map FIRST (keyed by :const:`ALERT_TYPE_OVERRIDE_KEY`), then falling
 * back to the ``--alert-type-*`` token via :func:`getAlertTypeColor`
 * (REQ-UI-088 / REQ-UI-046). An int without a stable key (``UNSPECIFIED`` /
 * unknown) is never overridable and resolves to its token.
 *
 * @param alertType - The SAPIENT ``AlertType`` integer.
 * @param overrides - The sparse ``map.alert.type_colors`` map.
 * @returns The override hex when present, else the live ``--alert-type-*`` token.
 */
export function resolveAlertTypeColor(alertType: number, overrides?: OverrideColors): string {
  const key = ALERT_TYPE_OVERRIDE_KEY[alertType];
  const override = key === undefined ? undefined : overrideOrUndefined(overrides, key);
  return override ?? getAlertTypeColor(alertType);
}

/**
 * Resolve a SAPIENT ``AlertPriority`` integer's colour, honouring a per-priority
 * override map FIRST (keyed by :const:`ALERT_PRIORITY_OVERRIDE_KEY`), then
 * falling back to the ``--alert-priority-*`` token via
 * :func:`getAlertPriorityColor`. The priority sibling of
 * :func:`resolveAlertTypeColor`.
 *
 * @param priority - The SAPIENT ``AlertPriority`` integer.
 * @param overrides - The sparse ``map.alert.priority_colors`` map.
 * @returns The override hex when present, else the live token.
 */
export function resolveAlertPriorityColor(priority: number, overrides?: OverrideColors): string {
  const key = ALERT_PRIORITY_OVERRIDE_KEY[priority];
  const override = key === undefined ? undefined : overrideOrUndefined(overrides, key);
  return override ?? getAlertPriorityColor(priority);
}

/**
 * Compose the MapLibre ``match`` expression for the alert glyph's
 * ``circle-color`` keyed on the feature's ``alert_type`` property (REQ-UI-088,
 * ``map.alert.color_mode === "by_alert_type"``; ADR-033 §9.7).
 *
 * Each ``AlertType`` enum stop maps to its **resolved** hex (via
 * :func:`getAlertTypeColor`), so MapLibre paints a data-driven severity colour
 * per feature without a client-side filter pass (ADR-016 §4 WebGL-at-scale). The
 * resolved-hex read means a theme flip re-composes the expression with the
 * other-theme tokens when the layer re-applies its paint (the same runtime CSS
 * read the platform/cluster colour expressions use). The default branch is the
 * UNSPECIFIED hue, so an unknown/absent ``alert_type`` still paints a legible
 * neutral (CLAUDE.md §8.1).
 *
 * # Per-type overrides (inc-items 4/5 — REQ-UI-088)
 *
 * ``overrides`` is the sparse ``map.alert.type_colors`` map: a stop whose type
 * has an operator override paints that hex; every other stop keeps its
 * ``--alert-type-*`` token. Resolved per stop via :func:`resolveAlertTypeColor`,
 * so an override change re-composes the expression and the alert layer re-paints
 * it in place (``setPaintProperty``) — never a teardown.
 *
 * @param overrides - The sparse ``map.alert.type_colors`` map (possibly absent).
 * @returns A MapLibre ``["match", ["get", "alert_type"], …]`` expression.
 *
 * Cross-references:
 *   - REQ-UI-088 / REQ-UI-046 / REQ-UI-094.
 *   - schemas/proto/meridian/v1/data/alert.proto §AlertType.
 */
export function alertTypeColorExpression(overrides?: OverrideColors): ExpressionSpecification {
  return [
    "match",
    ["get", "alert_type"],
    ALERT_TYPE.UNSPECIFIED,
    resolveAlertTypeColor(ALERT_TYPE.UNSPECIFIED, overrides),
    ALERT_TYPE.INFORMATION,
    resolveAlertTypeColor(ALERT_TYPE.INFORMATION, overrides),
    ALERT_TYPE.WARNING,
    resolveAlertTypeColor(ALERT_TYPE.WARNING, overrides),
    ALERT_TYPE.CRITICAL,
    resolveAlertTypeColor(ALERT_TYPE.CRITICAL, overrides),
    ALERT_TYPE.ERROR,
    resolveAlertTypeColor(ALERT_TYPE.ERROR, overrides),
    ALERT_TYPE.FATAL,
    resolveAlertTypeColor(ALERT_TYPE.FATAL, overrides),
    ALERT_TYPE.MODE_CHANGE,
    resolveAlertTypeColor(ALERT_TYPE.MODE_CHANGE, overrides),
    // Default branch — unknown/absent enum int paints the UNSPECIFIED neutral.
    resolveAlertTypeColor(ALERT_TYPE.UNSPECIFIED, overrides),
  ];
}

/**
 * Compose the MapLibre ``match`` expression for the alert glyph's
 * ``circle-color`` keyed on the feature's ``priority`` property (REQ-UI-088,
 * ``map.alert.color_mode === "by_priority"``; ADR-033 §9.7). The priority
 * sibling of :func:`alertTypeColorExpression`; same resolved-hex /
 * theme-reactive / neutral-default discipline.
 *
 * # Per-priority overrides (inc-items 4/5 — REQ-UI-088)
 *
 * ``overrides`` is the sparse ``map.alert.priority_colors`` map; same
 * override-first-else-token discipline as :func:`alertTypeColorExpression`, via
 * :func:`resolveAlertPriorityColor`.
 *
 * @param overrides - The sparse ``map.alert.priority_colors`` map (possibly
 *     absent).
 * @returns A MapLibre ``["match", ["get", "priority"], …]`` expression.
 *
 * Cross-references:
 *   - REQ-UI-088 / REQ-UI-046 / REQ-UI-094 / ADR-029 §5.
 *   - schemas/proto/meridian/v1/data/alert.proto §AlertPriority.
 */
export function alertPriorityColorExpression(overrides?: OverrideColors): ExpressionSpecification {
  return [
    "match",
    ["get", "priority"],
    ALERT_PRIORITY.UNSPECIFIED,
    resolveAlertPriorityColor(ALERT_PRIORITY.UNSPECIFIED, overrides),
    ALERT_PRIORITY.LOW,
    resolveAlertPriorityColor(ALERT_PRIORITY.LOW, overrides),
    ALERT_PRIORITY.MEDIUM,
    resolveAlertPriorityColor(ALERT_PRIORITY.MEDIUM, overrides),
    ALERT_PRIORITY.HIGH,
    resolveAlertPriorityColor(ALERT_PRIORITY.HIGH, overrides),
    ALERT_PRIORITY.CRITICAL,
    resolveAlertPriorityColor(ALERT_PRIORITY.CRITICAL, overrides),
    // Default branch — unknown/absent enum int paints the UNSPECIFIED neutral.
    resolveAlertPriorityColor(ALERT_PRIORITY.UNSPECIFIED, overrides),
  ];
}

/**
 * The alert glyph colour config the Map reads from the active Profile's
 * ``map.alert`` slice (ADR-033 §9.7; REQ-UI-088) and threads into the alert
 * glyph paint.
 *
 * - ``mode: "by_alert_type"`` (the Profile default) → data-driven colour keyed
 *   on the feature's ``alert_type`` (SAPIENT ``AlertType``;
 *   :func:`alertTypeColorExpression`).
 * - ``mode: "by_priority"`` → data-driven colour keyed on ``priority``
 *   (SAPIENT ``AlertPriority``; :func:`alertPriorityColorExpression`).
 * - ``mode: "single"`` → every alert glyph uses ``singleColor`` (fallback the
 *   ``--alert-glyph`` magenta token, which the Profile schema also defaults to).
 *
 * Both fields are optional so a call site without the working state gets the
 * ``by_alert_type`` default — a strict superset of the pre-inc6b flat-magenta
 * behaviour.
 *
 * Cross-references:
 *   - REQ-UI-088 (alert colour modes); ADR-033 §9.7.
 */
export interface AlertColorConfig {
  /** ``"by_alert_type"`` (default) | ``"by_priority"`` | ``"single"``. */
  mode?: "by_alert_type" | "by_priority" | "single";
  /** Profile ``map.alert.single_color`` hex — used only in ``single`` mode. */
  singleColor?: string;
  /**
   * Sparse ``map.alert.type_colors`` per-``AlertType`` overrides (keyed by
   * :const:`ALERT_TYPE_OVERRIDE_KEY`) — used only in ``by_alert_type`` mode
   * (inc-items 4/5; REQ-UI-088). Absent / empty ⇒ every type tracks its
   * ``--alert-type-*`` token.
   */
  typeColors?: OverrideColors;
  /**
   * Sparse ``map.alert.priority_colors`` per-``AlertPriority`` overrides (keyed
   * by :const:`ALERT_PRIORITY_OVERRIDE_KEY`) — used only in ``by_priority`` mode
   * (inc-items 4/5; REQ-UI-088). Absent / empty ⇒ every priority tracks its
   * ``--alert-priority-*`` token.
   */
  priorityColors?: OverrideColors;
}

/**
 * Compose the alert glyph's ``circle-color`` paint value for the active Profile
 * colour mode (REQ-UI-088; ADR-033 §9.7).
 *
 * Returns either a flat resolved hex (``single`` mode — fallback the
 * ``--alert-glyph`` magenta) or a data-driven ``match`` expression
 * (``by_alert_type`` / ``by_priority``). The expression keys on feature
 * properties the alert layer carries (``alert_type`` / ``priority``), so the
 * paint is evaluated per feature on the GPU (ADR-016 §4). All branches read the
 * theme-resolved tokens, so the alert layer's re-paint path picks up the active
 * theme on a flip (REQ-UI-040). An absent/unknown mode falls through to
 * ``by_alert_type`` (the Profile default).
 *
 * The active mode's per-value override map (``config.typeColors`` /
 * ``config.priorityColors``; inc-items 4/5, REQ-UI-088) is threaded into the
 * matching expression builder, so an operator's per-type / per-priority colour
 * wins over the token for that stop while the others keep tracking the theme.
 *
 * Returned typed as ``ExpressionSpecification`` for a uniform call site; a flat
 * colour string is a valid ``circle-color`` paint value (MapLibre accepts a
 * constant where an expression is allowed).
 *
 * @param config - The Profile-derived alert colour config (possibly empty).
 * @returns The ``circle-color`` paint value (flat hex or ``match`` expression).
 *
 * Cross-references:
 *   - REQ-UI-088 / REQ-UI-046 / REQ-UI-094 / REQ-UI-040; ADR-033 §9.7.
 */
export function alertColorExpression(config?: AlertColorConfig): ExpressionSpecification {
  const mode = config?.mode ?? "by_alert_type";
  if (mode === "single") {
    return resolveSingleColor(
      config?.singleColor,
      getAlertGlyphColor(),
    ) as unknown as ExpressionSpecification;
  }
  if (mode === "by_priority") {
    return alertPriorityColorExpression(config?.priorityColors);
  }
  return alertTypeColorExpression(config?.typeColors);
}
